import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, ListGroup, ListRow, Option, OptionList, Switch, TextField } from '@uplink/ui';
import { useState } from 'react';
import { errorText, useTRPC } from './api.ts';
import { usePrefs } from './prefs.tsx';
import { haptic } from './telegram.ts';

/** "ssh-ed25519 …AbCd1234": the type and the tail, enough to tell keys apart */
export const keyTail = (key: string) => {
  const [type, body] = key.split(' ');
  return `${type} …${body?.slice(-10) ?? ''}`;
};

/** Profile: the saved keys, with adding and deleting. */
export function SshKeysSection() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { t } = usePrefs();
  const keys = useQuery(trpc.sshKeys.list.queryOptions());
  const [adding, setAdding] = useState(false);
  const [value, setValue] = useState('');
  const refresh = () => queryClient.invalidateQueries(trpc.sshKeys.pathFilter());
  const add = useMutation(
    trpc.sshKeys.add.mutationOptions({
      onSuccess: () => {
        haptic.success();
        setValue('');
        setAdding(false);
        refresh();
      },
      onError: () => haptic.error(),
    }),
  );
  const remove = useMutation(trpc.sshKeys.remove.mutationOptions({ onSuccess: refresh }));

  return (
    <ListGroup header={t.profile.sshKeys} footer={t.profile.sshKeysFooter}>
      {(keys.data ?? []).map((k) => (
        <ListRow
          key={k.id}
          icon="key"
          title={k.name}
          subtitle={keyTail(k.publicKey)}
          trailing={
            <Button
              variant="ghost"
              loading={remove.isPending && remove.variables?.id === k.id}
              onClick={() => remove.mutate({ id: k.id })}
            >
              {t.profile.sshDelete}
            </Button>
          }
        />
      ))}
      {adding ? (
        <div className="ul-inset-form">
          <TextField
            id="ssh-new"
            label={t.serverCreate.ssh}
            value={value}
            onChange={setValue}
            placeholder={t.profile.sshPlaceholder}
            error={add.isError ? errorText(add.error) : undefined}
            hint={t.serverCreate.sshHint}
          />
          <Button
            block
            disabled={!value.trim()}
            loading={add.isPending}
            onClick={() => add.mutate({ publicKey: value })}
          >
            {t.profile.sshAdd}
          </Button>
        </div>
      ) : (
        <ListRow icon="plus" title={t.profile.sshAdd} chevron onClick={() => setAdding(true)} />
      )}
    </ListGroup>
  );
}

export type KeyChoice = { kind: 'none' } | { kind: 'saved'; publicKey: string } | { kind: 'new' };

/**
 * Server creation: pick a saved key, paste a new one (saved to the profile by default) or go without.
 * Returns the key to send with the order and a function to save a new one.
 */
export function useKeyPicker() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const keys = useQuery(trpc.sshKeys.list.queryOptions());
  const [choice, setChoice] = useState<KeyChoice | null>(null);
  const [value, setValue] = useState('');
  const [save, setSave] = useState(true);
  const addKey = useMutation(trpc.sshKeys.add.mutationOptions());

  // The first saved key is the default once the list arrives
  const first = keys.data?.[0];
  const current: KeyChoice =
    choice ?? (first ? { kind: 'saved', publicKey: first.publicKey } : { kind: 'none' });
  const publicKey =
    current.kind === 'saved'
      ? current.publicKey
      : current.kind === 'new' && value.trim()
        ? value.trim()
        : undefined;

  /** Saves a newly pasted key when asked to; never blocks the order */
  const remember = async () => {
    if (current.kind !== 'new' || !save || !publicKey) return;
    await addKey.mutateAsync({ publicKey }).catch(() => {});
    queryClient.invalidateQueries(trpc.sshKeys.pathFilter());
  };

  return { keys: keys.data ?? [], current, setChoice, value, setValue, save, setSave, publicKey, remember };
}

export function KeyPicker({
  picker,
  error,
}: {
  picker: ReturnType<typeof useKeyPicker>;
  error?: string;
}) {
  const { t } = usePrefs();
  const { keys, current, setChoice, value, setValue, save, setSave } = picker;
  return (
    <div className={`ul-stack${error ? ' ul-key-picker--error' : ''}`}>
      <OptionList label={t.serverCreate.sshPick}>
        {keys.map((k) => (
          <Option
            key={k.id}
            selected={current.kind === 'saved' && current.publicKey === k.publicKey}
            onSelect={() => setChoice({ kind: 'saved', publicKey: k.publicKey })}
            title={k.name}
            subtitle={keyTail(k.publicKey)}
          />
        ))}
        <Option
          selected={current.kind === 'new'}
          onSelect={() => setChoice({ kind: 'new' })}
          title={t.serverCreate.sshNew}
        />
        <Option
          selected={current.kind === 'none'}
          onSelect={() => setChoice({ kind: 'none' })}
          title={t.serverCreate.sshNone}
          subtitle={t.serverCreate.sshNoneHint}
        />
      </OptionList>
      {current.kind === 'new' ? (
        <>
          <TextField
            id="srv-ssh"
            label={t.serverCreate.ssh}
            placeholder="ssh-ed25519 AAAA…"
            value={value}
            onChange={setValue}
            hint={t.serverCreate.sshHint}
            error={error}
          />
          <div className="ul-setting ul-setting--inline">
            <span className="ul-setting__label">{t.serverCreate.sshSave}</span>
            <Switch checked={save} onChange={setSave} label={t.serverCreate.sshSave} />
          </div>
        </>
      ) : null}
      {error && current.kind !== 'new' ? <div className="ul-key-picker__error">{error}</div> : null}
    </div>
  );
}
